import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import {
  Pool,
  PoolTeam,
  WorldCupSurvivorEntryPicks,
  WorldCupSurvivorGroupPick,
  WorldCupSurvivorKnockoutPick,
  WorldCupKnockoutRoundKey,
  WORLD_CUP_KNOCKOUT_ROUNDS,
  WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT,
} from '../types/pool';
import { CountryFlag } from '../components/CountryFlag';
import { WorldCupTeamPicker, WorldCupView, WorldCupMatchup } from '../components/WorldCupTeamPicker';
import FeeNotice from '../components/FeeNotice';

const VIEW_STORAGE_KEY = 'wc-picker-view';

/**
 * World Cup Survivor Entry Form
 *
 * Two phases:
 *   Phase 1 (Group Stage) — pick exactly 6 teams expected to advance to R32.
 *   Phase 2 (Knockout)    — one team per round (R32, R16, QF, SF, Final).
 *
 * One-and-done across BOTH phases: a team used in Phase 1 cannot be re-used in Phase 2.
 *
 * The form picks the active phase by pool status:
 *   - OPEN  → Phase 1 (group-stage 6 picks).
 *   - LOCKED / IN_PROGRESS → Phase 2 (per-round single pick) for whichever round is upcoming.
 */
export function WorldCupSurvivorEntryForm() {
  const { poolId, entryId } = useParams<{ poolId: string; entryId?: string }>();
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  // Master World Cup pool events (homeTeam/awayTeam included). Used to render
  // a "Games" view of the actual round matchups in Phase 2 (rather than just
  // listing teams by group, which forces users to mentally locate each game).
  const [masterEvents, setMasterEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [entryName, setEntryName] = useState('');
  const [groupStageTeams, setGroupStageTeams] = useState<WorldCupSurvivorGroupPick[]>([]);
  const [knockoutPicks, setKnockoutPicks] = useState<
    Partial<Record<WorldCupKnockoutRoundKey, WorldCupSurvivorKnockoutPick>>
  >({});
  const [activeRound, setActiveRound] = useState<WorldCupKnockoutRoundKey>('R32');
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingPicks, setExistingPicks] = useState<WorldCupSurvivorEntryPicks | null>(null);

  // View tab — Seeds / Groups / Bracket. Shared with the Pick 6 form via the same
  // localStorage key so the user's preferred lens persists across both forms.
  const [view, setView] = useState<WorldCupView>(() => {
    if (typeof window === 'undefined') return 'groups';
    const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return saved === 'seeds' || saved === 'bracket' ? saved : 'groups';
  });
  function changeView(next: WorldCupView) {
    setView(next);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch {}
  }

  // Per-entry phase detection:
  //   GROUP    — pool is OPEN; user is still building their Phase 1 selection.
  //   WAITING  — pool is locked, but admin hasn't marked all 6 of this entry's
  //              group picks as advanced yet. We hide Phase 2 controls and show
  //              a "waiting on X groups" status banner.
  //   KNOCKOUT — pool is locked AND all 6 group picks have advanced (admin
  //              processed advancement for each pick's group). Phase 2 unlocked.
  //   (Eliminated entries are also shown an explicit eliminated banner via the
  //   existing isEliminated check further down.)
  const phase: 'GROUP' | 'WAITING' | 'KNOCKOUT' = (() => {
    if (!pool) return 'GROUP';
    if (pool.status === 'OPEN') return 'GROUP';
    // Once pool is locked, the entry can be in WAITING or KNOCKOUT depending on
    // how many of their picks have been advanced by admin. If no picks exist yet
    // (shouldn't happen post-lock, but defensive) fall back to WAITING.
    if (groupStageTeams.length === 0) return 'WAITING';
    const allAdvanced = groupStageTeams.every((t) => t.advanced === 'YES');
    return allAdvanced ? 'KNOCKOUT' : 'WAITING';
  })();
  // Teams in this entry that haven't been adjudicated yet — used to show
  // "Waiting on Argentina, Spain" etc. in the WAITING banner.
  const pendingGroupPicks = groupStageTeams.filter((t) => !t.advanced || t.advanced === 'PENDING');

  // Map the active round key (R32/R16/QF/SF/FINAL) to the event.round strings
  // the backend stores. Backend normalizes via normalizeWorldCupRound, but we
  // need to match the raw stored value here. Multiple aliases for QF/SF tolerate
  // both schedule imports and admin-typed values.
  const eventRoundAliases: Record<WorldCupKnockoutRoundKey, string[]> = {
    R32:   ['Round of 32'],
    R16:   ['Round of 16'],
    QF:    ['Quarter-Finals', 'Quarterfinals', 'Quarter Finals'],
    SF:    ['Semi-Finals', 'Semifinals', 'Semi Finals'],
    FINAL: ['Final'],
  };

  // Build the active round's matchups from master events. Each matchup carries
  // resolved PoolTeam references (so the picker can reuse its existing render
  // logic — flag, name, click-to-pick). Null sides render as TBD placeholders.
  const activeRoundMatchups: WorldCupMatchup[] = useMemo(() => {
    if (phase !== 'KNOCKOUT' || masterEvents.length === 0) return [];
    const aliases = new Set(eventRoundAliases[activeRound] || []);
    const teamById = new Map(teams.map((t) => [t.team.id, t]));
    // Resolve a Team from a master event's homeTeam/awayTeam to the user's PoolTeam.
    // The Team.id is shared across pools, so we can just look up by id.
    const resolve = (eventTeam: any): PoolTeam | null => {
      if (!eventTeam?.id) return null;
      return teamById.get(eventTeam.id) || null;
    };
    const list = masterEvents
      .filter((e) => aliases.has(e.round))
      .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
      .map((e, idx) => ({
        eventId: e.id,
        label: e.name || `Match ${idx + 1}`,
        scheduledAt: e.scheduledAt ?? null,
        home: resolve(e.homeTeam),
        away: resolve(e.awayTeam),
      }));
    return list;
  }, [phase, masterEvents, activeRound, teams]);

  useEffect(() => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      return;
    }

    async function loadData() {
      if (!poolId) return;
      try {
        setLoading(true);
        setError(null);

        const poolData = await api.getPool(poolId);
        setPool(poolData);
        setTeams(poolData.teams || []);

        // Fetch master WC events for the Games view. Best-effort — if it fails
        // we fall back to the static slot bracket view.
        try {
          const masterEventsRes = await api.getWorldCupMasterEvents();
          setMasterEvents(masterEventsRes.events || []);
        } catch (e) {
          console.warn('Could not load master WC events for Games view:', e);
        }

        if (entryId && isAuthenticated) {
          setIsEditMode(true);
          const token = await getAccessTokenSilently();
          const myEntries = await api.getMyEntries(poolId, token);
          const existingEntry = myEntries.find((e) => e.id === entryId);
          if (existingEntry) {
            setEntryName(existingEntry.entryName || '');
            const picks = existingEntry.picks as WorldCupSurvivorEntryPicks | undefined;
            if (picks) {
              setExistingPicks(picks);
              setGroupStageTeams(picks.groupStageTeams || []);
              setKnockoutPicks(picks.knockoutPicks || {});
            }
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load pool data');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [poolId, entryId, isAuthenticated, getAccessTokenSilently]);

  // First time we enter Phase 2 and matchups are available, auto-switch to the
  // Games view — that's the most useful lens for picking a per-round winner.
  // Tracked with a one-shot ref so we don't keep overriding the user's later
  // tab choice. (Doesn't fire if user explicitly picked Seeds/Groups earlier
  // and matchups haven't loaded yet, since we'd only flip from the saved view
  // to bracket once.)
  const [autoFlippedToGames, setAutoFlippedToGames] = useState(false);
  useEffect(() => {
    if (autoFlippedToGames) return;
    if (phase !== 'KNOCKOUT') return;
    if (activeRoundMatchups.length === 0) return;
    setView('bracket');
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, 'bracket'); } catch {}
    setAutoFlippedToGames(true);
  }, [phase, activeRoundMatchups.length, autoFlippedToGames]);

  // True when at least one matchup in the round has both teams resolved (not TBD).
  // Drives the smart-default round logic and the "matchups TBD" notice.
  function roundHasPlayableMatchups(roundKey: WorldCupKnockoutRoundKey): boolean {
    const aliases = new Set(eventRoundAliases[roundKey] || []);
    return masterEvents.some((e) => aliases.has(e.round) && e.homeTeam && e.awayTeam);
  }

  // Smart default for the active knockout round. Land on:
  //   1. The first round that has playable matchups AND no saved pick yet
  //      (this is "what the user came here to do").
  //   2. Falling back to the most recent round the user has a saved pick for
  //      (so they SEE their saved pick instead of landing on an all-TBD round
  //      and thinking the submission failed — what Paul hit in the screenshot).
  //   3. Falling back to R32 once masterEvents has loaded.
  //
  // One-shot via ref so we don't yank the user back to a different tab after
  // they click around — defaulting happens only on first entry to Phase 2.
  const knockoutDefaultedRef = useRef(false);
  useEffect(() => {
    if (knockoutDefaultedRef.current) return;
    if (phase !== 'KNOCKOUT') return;
    const playableUnpicked = WORLD_CUP_KNOCKOUT_ROUNDS.find(
      (r) => roundHasPlayableMatchups(r.key) && !knockoutPicks[r.key],
    );
    if (playableUnpicked) {
      setActiveRound(playableUnpicked.key);
      knockoutDefaultedRef.current = true;
      return;
    }
    const lastSaved = [...WORLD_CUP_KNOCKOUT_ROUNDS].reverse().find((r) => knockoutPicks[r.key]);
    if (lastSaved) {
      setActiveRound(lastSaved.key);
      knockoutDefaultedRef.current = true;
      return;
    }
    // Only lock in the R32 default once masterEvents has loaded, so we don't
    // commit to a fallback before knowing what's playable.
    if (masterEvents.length > 0) {
      setActiveRound('R32');
      knockoutDefaultedRef.current = true;
    }
  }, [phase, masterEvents, knockoutPicks]);

  // True when the user has changed any knockout pick relative to what's saved
  // server-side. Drives the Submit button — gating on activeRound alone (the
  // old behavior) meant a user with a fresh R32 pick couldn't submit while
  // viewing the R16 tab. With this, "any pending change anywhere" enables submit.
  const hasUnsavedKnockoutChanges = useMemo(() => {
    if (phase !== 'KNOCKOUT') return false;
    const saved = existingPicks?.knockoutPicks || {};
    const keys = new Set<WorldCupKnockoutRoundKey>([
      ...(Object.keys(saved) as WorldCupKnockoutRoundKey[]),
      ...(Object.keys(knockoutPicks) as WorldCupKnockoutRoundKey[]),
    ]);
    for (const k of keys) {
      if (saved[k]?.teamId !== knockoutPicks[k]?.teamId) return true;
    }
    return false;
  }, [phase, existingPicks, knockoutPicks]);

  // Is the currently-displayed round's pick already saved server-side?
  // Used to render a "saved" badge above the picker so users have confidence
  // their submission actually landed.
  const activeRoundIsSaved = useMemo(() => {
    if (phase !== 'KNOCKOUT') return false;
    const savedPick = existingPicks?.knockoutPicks?.[activeRound]?.teamId;
    const localPick = knockoutPicks[activeRound]?.teamId;
    return !!savedPick && savedPick === localPick;
  }, [phase, existingPicks, knockoutPicks, activeRound]);

  // All matchups for active round exist but have no teams resolved yet (TBD).
  // When true, we show a notice explaining the round won't open until prior
  // round games are played, so the user doesn't think the page is broken.
  const activeRoundAllTbd = useMemo(() => {
    if (phase !== 'KNOCKOUT') return false;
    if (activeRoundMatchups.length === 0) return false;
    return activeRoundMatchups.every((m) => !m.home && !m.away);
  }, [phase, activeRoundMatchups]);


  // Set of all team IDs used (across both phases) — drives one-and-done rule
  const usedTeamIds = useMemo(() => {
    const s = new Set<string>();
    groupStageTeams.forEach((t) => s.add(t.teamId));
    Object.values(knockoutPicks).forEach((p) => p && s.add(p.teamId));
    return s;
  }, [groupStageTeams, knockoutPicks]);

  function tierColor(seed: number) {
    if (seed <= 4) return 'bg-emerald-50 border-emerald-300 text-emerald-900';
    if (seed <= 8) return 'bg-sky-50 border-sky-300 text-sky-900';
    return 'bg-rose-50 border-rose-300 text-rose-900';
  }

  function isTeamPickedInGroup(teamId: string) {
    return groupStageTeams.some((t) => t.teamId === teamId);
  }

  function toggleGroupStageTeam(team: PoolTeam) {
    const id = team.team.id;
    if (isTeamPickedInGroup(id)) {
      setGroupStageTeams((prev) => prev.filter((t) => t.teamId !== id));
    } else if (groupStageTeams.length < WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT) {
      setGroupStageTeams((prev) => [
        ...prev,
        {
          teamId: team.team.id,
          teamName: team.team.name,
          shortName: team.team.shortName,
          seed: team.seed ?? 12,
          isoCode: (team.metadata?.isoCode as string) ?? '',
          advanced: 'PENDING',
        },
      ]);
    }
  }

  function setKnockoutPick(round: WorldCupKnockoutRoundKey, team: PoolTeam) {
    // Toggle: clicking the already-selected team clears the pick. Without this
    // the picker had no way to deselect via click — only the small "Clear pick"
    // link, which was easy to miss.
    if (knockoutPicks[round]?.teamId === team.team.id) {
      clearKnockoutPick(round);
      return;
    }
    // Block one-and-done re-use
    if (usedTeamIds.has(team.team.id)) {
      setError(`${team.team.name} has already been used earlier in the pool.`);
      return;
    }
    setError(null);
    setKnockoutPicks((prev) => ({
      ...prev,
      [round]: {
        teamId: team.team.id,
        teamName: team.team.name,
        shortName: team.team.shortName,
        seed: team.seed ?? 12,
        isoCode: (team.metadata?.isoCode as string) ?? '',
        result: 'PENDING',
      },
    }));
  }

  function clearKnockoutPick(round: WorldCupKnockoutRoundKey) {
    setKnockoutPicks((prev) => {
      const next = { ...prev };
      delete next[round];
      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (pool && new Date(pool.deadline) < new Date() && phase === 'GROUP') {
      setError('The deadline for this pool has passed. You can no longer submit Phase 1 picks.');
      return;
    }

    if (phase === 'GROUP' && groupStageTeams.length !== WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT) {
      setError(`Please select exactly ${WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT} group-stage teams`);
      return;
    }

    const hasEntryFee = pool?.entryFee && Number(pool.entryFee) > 0;

    try {
      setSubmitting(true);
      setError(null);
      const token = await getAccessTokenSilently();

      const picks: WorldCupSurvivorEntryPicks = {
        type: 'WORLD_CUP_SURVIVOR',
        status: existingPicks?.status ?? 'ALIVE',
        eliminatedRound: existingPicks?.eliminatedRound ?? null,
        groupStageTeams,
        knockoutPicks,
      };

      const entryData: any = { entryName, picks };

      if (isEditMode && entryId) {
        await api.updateEntry(poolId!, entryId, entryData, token);
        navigate(`/pools/${poolId}`);
      } else {
        await api.createEntry(poolId!, entryData, token);
        if (hasEntryFee) {
          // Payment v2: entry is saved — take them to Settle Up to pay
          navigate('/settle-up', {
            state: { justSubmitted: true, submittedPool: pool?.name },
          });
        } else {
          navigate(`/pools/${poolId}`);
        }
      }
    } catch (err: any) {
      console.error('Failed to submit entry:', err);
      let errorMessage: string;
      if (
        err?.message?.includes('login_required') ||
        err?.message?.includes('Missing Refresh Token') ||
        err?.error === 'login_required' ||
        err?.message?.includes('Login required')
      ) {
        errorMessage = 'Your session has expired. Please log in again to submit your entry.';
        setTimeout(() => {
          loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        }, 2000);
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to submit entry';
      }
      setError(errorMessage);
      setTimeout(() => {
        document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !pool) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
          <Link to="/pools" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Pools
          </Link>
        </div>
      </div>
    );
  }

  if (!pool) return null;

  const isDeadlinePassed = new Date(pool.deadline) < new Date();
  const isEliminated = existingPicks?.status === 'ELIMINATED';

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto">
      <div className="mb-6">
        <Link to={`/pools/${poolId}`} className="text-blue-600 hover:underline text-sm">
          ← Back to {pool.name}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {phase === 'GROUP'
            ? isEditMode
              ? 'Edit Your Group-Stage Picks'
              : 'Phase 1: Pick Your Group-Stage Survivors'
            : phase === 'WAITING'
            ? 'Phase 2 — Waiting on Group Stage'
            : "Phase 2: Make Today's Knockout Pick"}
        </h1>
        <p className="text-gray-600 mt-1">
          {phase === 'GROUP'
            ? 'Pick 6 teams that will all advance to the Round of 32. If any one fails to advance, you are eliminated.'
            : phase === 'WAITING'
            ? 'Your Phase 2 picks unlock as soon as all six of your group-stage picks are confirmed advancing.'
            : 'Pick one team to win their match outright. If they lose, you are eliminated. One-and-done across the entire pool.'}
        </p>
      </div>

      {isEliminated && (
        <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4 text-red-800">
          <p className="font-bold">❌ Entry eliminated</p>
          <p className="text-sm">
            This entry was eliminated{existingPicks?.eliminatedRound ? ` in ${existingPicks.eliminatedRound}` : ''}.
          </p>
        </div>
      )}

      {phase === 'GROUP' && isDeadlinePassed && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">⚠️ Phase 1 Deadline Passed</p>
          <p className="text-sm">
            Group-stage picks closed at {new Date(pool.deadline).toLocaleString()}.
          </p>
        </div>
      )}

      {/* Used Teams Recap (always visible — drives one-and-done awareness) */}
      {(groupStageTeams.length > 0 || Object.keys(knockoutPicks).length > 0) && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Teams already used in this entry</h3>
          {groupStageTeams.length > 0 && (
            <div className="mb-3">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Phase 1 — Group Stage</p>
              <div className="flex flex-wrap gap-2">
                {groupStageTeams.map((t) => (
                  <span
                    key={t.teamId}
                    className={`pl-2 pr-3 py-1.5 rounded-lg border flex items-center gap-2 text-sm ${tierColor(t.seed)}`}
                  >
                    <CountryFlag iso={t.isoCode} alt={t.teamName} size="xs" rounded />
                    <span className="font-medium">{t.teamName}</span>
                    {t.advanced === 'YES' && <span className="text-emerald-600">✓ advanced</span>}
                    {t.advanced === 'NO' && <span className="text-red-600">✗ out</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(knockoutPicks).length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Phase 2 — Knockout</p>
              <div className="flex flex-wrap gap-2">
                {WORLD_CUP_KNOCKOUT_ROUNDS.filter((r) => knockoutPicks[r.key]).map((r) => {
                  const p = knockoutPicks[r.key]!;
                  return (
                    <span
                      key={r.key}
                      className={`pl-2 pr-3 py-1.5 rounded-lg border flex items-center gap-2 text-sm ${tierColor(p.seed)}`}
                    >
                      <span className="text-xs uppercase tracking-wide text-gray-500">{r.label}:</span>
                      <CountryFlag iso={p.isoCode} alt={p.teamName} size="xs" rounded />
                      <span className="font-medium">{p.teamName}</span>
                      {p.result === 'WIN' && <span className="text-emerald-600">✓ won</span>}
                      {p.result === 'LOSS' && <span className="text-red-600">✗ lost</span>}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Entry Name */}
        {phase === 'GROUP' && !isEditMode && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Entry Name (optional)</label>
            <input
              type="text"
              value={entryName}
              onChange={(e) => setEntryName(e.target.value)}
              placeholder="My Survivor Entry"
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              disabled={isDeadlinePassed}
            />
          </div>
        )}

        {phase === 'WAITING' ? (
          <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-lg p-6">
            <p className="text-lg font-semibold text-amber-900 mb-2">⏳ Waiting on group stage to resolve</p>
            <p className="text-sm text-amber-800 mb-4">
              Your Phase 2 knockout picks unlock once all six of your group-stage picks are confirmed
              as advancing to the Round of 32. Admin will mark teams as their groups finish — check
              back periodically.
            </p>
            {pendingGroupPicks.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-amber-700 mb-2">
                  Still waiting on {pendingGroupPicks.length} pick{pendingGroupPicks.length !== 1 ? 's' : ''}:
                </p>
                <div className="flex flex-wrap gap-2">
                  {pendingGroupPicks.map((t) => (
                    <span
                      key={t.teamId}
                      className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-amber-300 rounded text-sm"
                    >
                      {t.isoCode && <CountryFlag iso={t.isoCode} alt={t.teamName} size="xs" rounded />}
                      <span>{t.teamName}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : phase === 'GROUP' ? (
          <>
            <div className="mb-4 flex items-center justify-between">
              <div className="text-lg font-semibold">
                Selected:{' '}
                <span className={groupStageTeams.length === WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT ? 'text-green-600' : 'text-emerald-700'}>
                  {groupStageTeams.length}/{WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT}
                </span>
              </div>
              <p className="text-sm text-gray-600">All 6 must advance to R32 or you're out.</p>
            </div>

            {teams.length === 0 ? (
              <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-lg p-6 text-center">
                <span className="text-3xl block mb-2">⚽</span>
                <h3 className="font-semibold text-gray-900 mb-2">World Cup Field Not Yet Available</h3>
                <p className="text-gray-600">Teams will appear here once an admin imports the 48-nation field.</p>
              </div>
            ) : (
              <div className="mb-8">
                <WorldCupTeamPicker
                  teams={teams}
                  selectedTeamIds={new Set(groupStageTeams.map((t) => t.teamId))}
                  maxSelections={WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT}
                  onToggle={toggleGroupStageTeam}
                  readOnly={isDeadlinePassed}
                  view={view}
                  onViewChange={changeView}
                  helpText="Pick 6 nations that will all advance to the Round of 32. View by FIFA seed, by group, or by bracket position."
                />
              </div>
            )}
          </>
        ) : (
          <>
            {/* Round Selector */}
            <div className="mb-4 flex flex-wrap gap-2">
              {WORLD_CUP_KNOCKOUT_ROUNDS.map((r) => {
                const picked = !!knockoutPicks[r.key];
                const isActive = activeRound === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setActiveRound(r.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      isActive
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : picked
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {r.label}
                    {picked && <span className="ml-1">✓</span>}
                  </button>
                );
              })}
            </div>

            <div className="mb-3 text-sm text-gray-600">
              Pick one team for <strong>{WORLD_CUP_KNOCKOUT_ROUNDS.find((r) => r.key === activeRound)?.label}</strong>.
              Teams already used (Phase 1 or earlier rounds) are disabled.
              {knockoutPicks[activeRound] && (
                <button
                  type="button"
                  onClick={() => clearKnockoutPick(activeRound)}
                  className="ml-2 text-red-600 hover:underline"
                >
                  Clear pick
                </button>
              )}
            </div>

            {/* Saved-pick banner: confirms the server has the current selection.
                Critical UX: without this, a returning user sees the pick highlighted
                in the picker but no signal that it was actually saved, which led
                Paul to think the R32 submission failed. */}
            {activeRoundIsSaved && (
              <div className="mb-3 bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm text-emerald-900 flex items-center gap-2">
                <span className="text-lg">✓</span>
                <span>
                  Your <strong>{WORLD_CUP_KNOCKOUT_ROUNDS.find((r) => r.key === activeRound)?.label}</strong> pick
                  (<strong>{knockoutPicks[activeRound]?.teamName}</strong>) is saved. Pick a different team and
                  hit Save to change it, or move to a later round.
                </span>
              </div>
            )}

            {/* TBD notice: when admin hasn't yet populated teams for this round
                (e.g. R16 before R32 games have been played), explain that the
                matchups will appear later. Prevents the "wall of TBD cards"
                from looking like a broken page. */}
            {activeRoundAllTbd && (
              <div className="mb-3 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900">
                ⏳ <strong>{WORLD_CUP_KNOCKOUT_ROUNDS.find((r) => r.key === activeRound)?.label}</strong> matchups
                will appear here once the prior round's games are played and admin sets the teams. You can
                still pre-pick from Seeds or Groups view, but you won't see the head-to-head matchups yet.
              </div>
            )}

            <div className="mb-8">
              <WorldCupTeamPicker
                teams={teams}
                // Selected = the current round's pick (only one). maxSelections=1 enforces "single pick per round".
                selectedTeamIds={
                  new Set(knockoutPicks[activeRound] ? [knockoutPicks[activeRound]!.teamId] : [])
                }
                // Disabled = teams used in Phase 1 OR earlier knockout rounds (one-and-done across both phases),
                // EXCEPT the current pick itself (so the user can deselect/replace it).
                disabledTeamIds={
                  new Set(
                    Array.from(usedTeamIds).filter(
                      (id) => id !== knockoutPicks[activeRound]?.teamId,
                    ),
                  )
                }
                maxSelections={1}
                onToggle={(team) => setKnockoutPick(activeRound, team)}
                readOnly={isEliminated}
                view={view}
                onViewChange={changeView}
                matchups={activeRoundMatchups}
                helpText="Pick one team for this round. Teams used earlier in your entry are disabled (one-and-done)."
              />
            </div>
          </>
        )}

        {/* Entry fee notice — payment happens after submit (Settle Up) */}
        {phase === 'GROUP' && pool.entryFee && Number(pool.entryFee) > 0 && !isEditMode && (
          <div className="mb-6">
            <FeeNotice entryFee={Number(pool.entryFee)} />
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border-2 border-red-300 rounded-lg p-4 text-red-800 font-medium" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-4">
          {/* Submit button hidden in WAITING phase — there's nothing to submit until
              admin advances all the entry's group picks. */}
          {phase !== 'WAITING' && (
            <button
              type="submit"
              disabled={
                submitting ||
                isEliminated ||
                (phase === 'GROUP' &&
                  (isDeadlinePassed ||
                    groupStageTeams.length !== WORLD_CUP_SURVIVOR_GROUP_PICK_COUNT)) ||
                // Knockout: enable whenever there's ANY pending change vs. saved state,
                // regardless of which tab is currently active. Previously we gated on
                // !knockoutPicks[activeRound], which meant a user with a fresh R32 pick
                // couldn't submit if they navigated to the R16 tab.
                (phase === 'KNOCKOUT' && !hasUnsavedKnockoutChanges)
              }
              className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {submitting
                ? 'Submitting...'
                : phase === 'GROUP'
                ? isEditMode
                  ? 'Update Group-Stage Picks'
                  : 'Submit Entry'
                : hasUnsavedKnockoutChanges
                ? 'Save Pick'
                : 'All picks saved'}
            </button>
          )}
          <Link
            to={`/pools/${poolId}`}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            {phase === 'WAITING' ? 'Back to pool' : 'Cancel'}
          </Link>
        </div>
      </form>

      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">💡 Survivor Strategy</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Phase 1: Six picks, all must advance — heavy weighting toward seeds 1–4 is the safe play.</li>
          <li>• Phase 2: One-and-done. Save your top seeds for later rounds when matchups get tougher.</li>
          <li>• Don't burn a Seed 1 in the Round of 32 unless you have to — they're priceless in the SF/Final.</li>
          <li>• If you have no unused team available for a round, you're auto-eliminated.</li>
        </ul>
      </div>
    </div>
  );
}

export default WorldCupSurvivorEntryForm;
